import React, { FC, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useApolloClient } from '@apollo/client'
import InfiniteScroll from 'react-infinite-scroll-component'
import { debounce } from 'lodash'
import useCaptions from '../../redux/caption-library/useCaptions'
import { search as searchQuery } from '../../graphql/CaptionSet/captionSearchQuery'
import LibraryCaption from './LibraryCaption'
import { useNotifications } from '../Notifications'
import { EmptyState } from '../EmptyState'
import { StyledCaptionList } from '.'
import { SkeletonLoader } from '../SkeletonLoader'
import useSegment from '../utils/useSegment'
import { SearchCaptions } from '../../graphql/CaptionSet/__generated__/SearchCaptions'

const CaptionList: FC = () => {
  const [totalCount, setTotalCount] = useState(0)
  const { track } = useSegment()
  const apollo = useApolloClient()
  const { addNotification } = useNotifications()
  const {
    captions,
    search,
    categories,
    loading,
    hideUsed,
    onlyFavorites,
    sort,
    page,
    actions: { addCaptions, changePage }
  } = useCaptions()

  // Sends the search event to Segment
  const handleSearchTrack = debounce((resultsCount: number) => {
    track('Searched Caption Library', {
      page,
      search,
      sorted_by: sort,
      liked_filter: onlyFavorites,
      used_filter: hideUsed,
      category_filter: categories,
      search_results_count: resultsCount
    })
  }, 3000)

  useEffect(() => {
    const handleCaptions = async () => {
      try {
        const { data } = await apollo.query<SearchCaptions>({
          fetchPolicy: 'no-cache',
          query: searchQuery,
          variables: {
            page,
            search,
            liked: onlyFavorites,
            categories,
            sort: ['newest', 'suggested'].includes(sort) ? sort : 'newest',
            hideUsed
          }
        })

        addCaptions(data.captionSetSearch?.docs || [])
        setTotalCount(data?.captionSetSearch?.totalDocs || 0)
        handleSearchTrack(data?.captionSetSearch?.totalDocs || 0)
      }
      catch (err) {
        addNotification({ message: err.message, type: 'error', interval: 10000 })
        Sentry.captureException(err)
      }
    }

    if (loading) {
      handleCaptions()
    }
  }, [loading])

  return (
    <StyledCaptionList>
      <InfiniteScroll
        scrollThreshold={0.7}
        dataLength={captions.length}
        next={() => changePage(page + 1)}
        hasMore={captions.length < totalCount}
        loader={null}
      >
        <div className="caption-list">
          {loading && captions.length === 0 && <SkeletonLoader type="caption-list-item" />}
          {captions.map((caption) => (
            <LibraryCaption key={caption._id} caption={caption} className="caption-library-item" />
          ))}
        </div>
      </InfiniteScroll>

      {!loading && captions.length === 0 && (
        <EmptyState
          title="Whoops, nothing came up."
          suggestion="Try filtering by category or searching for keywords in the search bar."
        />
      )}
    </StyledCaptionList>
  )
}

export default CaptionList
